import type {
  PaymentCreateChargeType,
  PaymentCreateDirection,
  PaymentCreatePaymentType,
  PaymentCreateStatus,
} from '#/shared/api/generated/billing/models'
import { PaymentMethodResponseType } from '#/shared/api/generated/billing/models'

export type PaymentType = Exclude<PaymentCreatePaymentType, null | undefined>
export type PaymentDirection = Exclude<PaymentCreateDirection, null | undefined>
export type PaymentMethod = Exclude<PaymentCreateChargeType, null | undefined>
export type PaymentMethodType = PaymentMethodResponseType
export type PaymentStatus = Exclude<PaymentCreateStatus, null | undefined>

export const PAYMENT_TYPE = {
  income: 'income',
  refund: 'refund',
  carrier_pay: 'carrier_pay',
  tip: 'tip',
} as const satisfies Record<PaymentType, PaymentType>

export const PAYMENT_DIRECTION = {
  customer_to_broker: 'customer_to_broker',
  broker_to_customer: 'broker_to_customer',
  broker_to_carrier: 'broker_to_carrier',
} as const satisfies Record<PaymentDirection, PaymentDirection>

export const PAYMENT_METHOD = {
  zelle: 'zelle',
  ach: 'ach',
  wire: 'wire',
  card: 'card',
  check: 'check',
  cash: 'cash',
  venmo: 'venmo',
  cashapp: 'cashapp',
  paypal: 'paypal',
} as const satisfies Record<PaymentMethod, PaymentMethod>

export const PAYMENT_STATUS = {
  created: 'created',
} as const satisfies Record<'created', PaymentStatus>

// Card gateways aren't charge types — a payment through any of them is
// recorded as a `card` charge.
const GATEWAY_CHARGE_TYPE: Partial<Record<PaymentMethodType, PaymentMethod>> = {
  [PaymentMethodResponseType.stripe]: PAYMENT_METHOD.card,
  [PaymentMethodResponseType.authorize_net]: PAYMENT_METHOD.card,
  [PaymentMethodResponseType.firstdata]: PAYMENT_METHOD.card,
  [PaymentMethodResponseType.quickbooks]: PAYMENT_METHOD.card,
  [PaymentMethodResponseType.square]: PAYMENT_METHOD.card,
}

export function methodToChargeType(
  method: PaymentMethodType | undefined,
): PaymentMethod | undefined {
  if (!method) return undefined
  return GATEWAY_CHARGE_TYPE[method] ?? (method as PaymentMethod)
}

/**
 * True for every method that is charged as a card — the plain `card` type and
 * each merchant gateway. A payment runs through a single merchant, so these
 * are mutually exclusive in the picker even though methods are multi-select.
 */
export function isCardChargeMethod(method: string): boolean {
  return methodToChargeType(method as PaymentMethodType) === PAYMENT_METHOD.card
}
